/**
 * What a binding is called on the Controls screen (08 §3). Bindings are stored as
 * `KeyboardEvent.code` (physical keys, so WASD stays WASD on any layout), `Mouse0`.. for mouse
 * buttons and `WheelUp` / `WheelDown`; the player reads `W`, `SHIFT`, `LMB`.
 *
 * The names themselves are the input layer's (`keyLabel`, src/input/bindings): the HUD's prompts
 * and the store's own refusals ("SHIFT is already bound to...") use them, and a key must not be
 * called one thing on a button and another in the message beside it. This module adds only what
 * the screen needs on top: an empty slot, a list, and the code a mouse button is stored under.
 * Pure. `keyLabel` is the one thing the menus import from src/input.
 */

import { keyLabel } from '../../../input/bindings';

/** The label for an empty slot. */
export const UNBOUND = '---';

/** `KeyW` -> `W`, `Digit5` -> `5`, `Mouse0` -> `LMB`; `---` for an empty slot. */
export function keyName(code: string): string {
  const c = code.trim();
  return c === '' ? UNBOUND : keyLabel(c);
}

/** The code a mouse button is stored under: `Mouse0` is the left button. */
export function mouseCode(button: number): string {
  return `Mouse${Math.max(0, Math.round(button))}`;
}

/** All of an action's keys on one line: `W / UP`, or `---` when it has none. */
export function keyList(keys: readonly string[]): string {
  const named = keys.filter((k) => k.trim() !== '').map(keyName);
  return named.length === 0 ? UNBOUND : named.join(' / ');
}
